import { createContext, useContext, type ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { rumSessionId } from '@/lib/rum';
import { chooseAction, type Failure } from '@/lib/failure';

//   page   -- the whole view is gone
//   panel  -- one region is gone, keeping its footprint so nothing reflows into
//             the gap and back out again on recovery
//   inline -- a line of detail is missing from something still usable
export type ErrorSize = 'page' | 'panel' | 'inline';

export function ErrorShell({ children }: { children: ReactNode }) {
  return <div className="mx-auto max-w-2xl p-6">{children}</div>;
}

// Reloading rebuilds the document, which on a page holding a call in the
// browser destroys it -- and nothing here asks before unloading. So while that
// is true, no error screen may offer it, however deep in the tree it sits.
// Set once by whatever knows a call is up; every screen below reads it, which
// is the only way a boundary added later cannot reintroduce the button.
const ReloadIsDestructive = createContext(false);

export function ReloadGuard({ active, children }: { active: boolean; children: ReactNode }) {
  return <ReloadIsDestructive.Provider value={active}>{children}</ReloadIsDestructive.Provider>;
}

export function ErrorState({
  failure,
  size,
  onRetry,
  children,
}: {
  failure: Failure;
  size: ErrorSize;
  onRetry?: () => void;
  children?: ReactNode;
}) {
  // The choice is a pure function so it can be tested without rendering; this
  // only draws the answer.
  const choice = chooseAction(failure.recovery, onRetry != null, useContext(ReloadIsDestructive));
  const action =
    choice === 'retry' ? (
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    ) : choice === 'reload' ? (
      <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
        Reload the page
      </Button>
    ) : null;

  if (size === 'inline') {
    return (
      <p className="bg-muted/40 text-muted-foreground flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-sm">
        <span>{failure.message}</span>
        {action}
        {children}
      </p>
    );
  }

  return (
    <div
      className={cn(
        'border-destructive/40 bg-destructive/5 rounded-lg border text-sm',
        size === 'page' ? 'p-6' : 'p-4',
      )}
      // A region that vanishes and is replaced mid-task is what a screen reader
      // user otherwise misses.
      role="alert"
    >
      <div className="flex items-start gap-2">
        <TriangleAlert className="text-destructive mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-destructive font-medium">{failure.message}</p>
          {/* A refusal offers nothing and has no children; an always-present row
              leaves a gap that reads as something failing to load. */}
          {(action != null || children != null) && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {action}
              {children}
            </div>
          )}
          <FailureDetail failure={failure} />
        </div>
      </div>
    </div>
  );
}

// For the person relaying the problem, not the person hitting it. The session
// id is omitted rather than blank when there is none, so nobody quotes an empty
// label.
function FailureDetail({ failure }: { failure: Failure }) {
  const session = rumSessionId();
  if (failure.detail == null && session == null) return null;

  return (
    <details className="mt-3">
      <summary className="text-muted-foreground cursor-pointer text-xs select-none">
        Details for support
      </summary>
      <div className="text-muted-foreground mt-1.5 space-y-1 text-xs">
        {failure.detail != null && <p className="font-mono break-all">{failure.detail}</p>}
        {session != null && (
          <p>
            Session <span className="font-mono">{session}</span>
          </p>
        )}
      </div>
    </details>
  );
}
